import React, { ReactNode } from 'react';
import { SelectableCard } from '../SelectableCard';

/**
 * One leg of a booking — where we collect from, where we return to, when the
 * move happens. A card carrying its own heading, its questions and the notes
 * that qualify them.
 *
 * Replaces the page-local `CollapsibleSection`, which was a `<div onClick>`
 * wrapping an unnamed icon `<button>`: no `role`, no `aria-expanded`, no
 * `aria-controls`, and a collapsed body that stayed in the tab order and in
 * the accessibility tree behind `max-height: 0`, so Tab walked into address
 * fields nobody could see. The collapse also ran on `overflow: hidden`, which
 * clipped the address-suggestion listbox — patched around once already in
 * `_address-lookup-v2.scss`.
 *
 * A leg the customer may skip carries a `choice` instead: two radio options,
 * with the fields revealed under the one that needs them. This is the
 * conditional-reveal pattern, and it is deliberately not a disclosure or a
 * switch. Both outcomes here are real answers — "here is the address" and
 * "ask me later" are equally complete ways to finish the step — and a
 * show/hide control can only render one of them as a state and the other as
 * an absence. Given an empty card and a line of grey text, the customer
 * cannot tell whether they have answered or merely not noticed the question.
 * Two options, one of them selected, always says which.
 *
 * Where the answer is required there is no control at all — hiding a required
 * question buys nothing and costs the customer the overview of the step.
 */
export interface BookingLegChoice {
  /** Radio-group name. Must be unique on the page. */
  name: string;
  /** Picking this answers the leg now, and reveals the body. */
  onLabel: string;
  onDescription?: ReactNode;
  /** Picking this defers the leg. Say what happens instead. */
  offLabel: string;
  offDescription?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

interface BookingLegProps {
  /** Position in the step, shown as a numeral chip. */
  step: number;
  title: string;
  /** One line on what this leg decides. */
  lead?: ReactNode;
  status?: 'complete' | 'optional' | null;
  /** Base for the generated title / body ids. */
  id: string;
  choice?: BookingLegChoice;
  children: ReactNode;
}

export default function BookingLeg({
  step,
  title,
  lead,
  status = null,
  id,
  choice,
  children,
}: BookingLegProps): JSX.Element {
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  const on = !choice || choice.checked;

  return (
    <section
      className={`booking-leg${
        status === 'complete' ? ' booking-leg--complete' : ''
      }${choice && !on ? ' booking-leg--deferred' : ''}`}
      aria-labelledby={titleId}
    >
      <div className="booking-leg__head">
        <span className="booking-leg__step" aria-hidden="true">
          {step}
        </span>

        <h2 className="booking-leg__title" id={titleId}>
          {title}
        </h2>

        {status && (
          <span className={`booking-leg__badge booking-leg__badge--${status}`}>
            {status === 'complete' ? 'Complete' : 'Optional'}
          </span>
        )}
      </div>

      {lead && <p className="booking-leg__lead">{lead}</p>}

      {choice && (
        <div className="booking-leg__choice">
          <SelectableCard
            variant="single"
            name={choice.name}
            value="now"
            checked={choice.checked}
            onChange={() => choice.onChange(true)}
            label={choice.onLabel}
            description={choice.onDescription}
            aria-controls={bodyId}
          />
          <SelectableCard
            variant="single"
            name={choice.name}
            value="later"
            checked={!choice.checked}
            onChange={() => choice.onChange(false)}
            label={choice.offLabel}
            description={choice.offDescription}
          />
        </div>
      )}

      {/*
        `hidden` rather than a max-height collapse. The animated version left
        the fields focusable and readable by assistive tech while invisible,
        and its `overflow: hidden` clipped the address listbox.
      */}
      <div className="booking-leg__body" id={bodyId} hidden={!on}>
        {children}
      </div>
    </section>
  );
}
